import type { RefObject } from 'react';
import { fileToBase64 } from './fileUtils';
import { StepImageDimensions, ZoneConfig, ZoneImageState } from '../types';
import type { DoodleCanvasRef } from '../ui/DoodleCanvas';
import {
  logError,
  loadImage,
  setupCanvas,
  calculateImageScaling,
  renderUserImageWithMask,
  renderUserImageWithoutMask
} from './canvasRenderer';
import { loadPNGMask, calculatePNGMaskPositioning, applyPNGMaskClipping } from './pngProcessor';
import { renderAllZones } from './multiZoneRenderer';
import { drawMaskOutlineOnCanvas } from './maskOutline';
import { supportsWebP } from '../../../utilities/webpDetection';

const imageType = supportsWebP() ? 'image/webp' : 'image/png';

/**
 * Handles the save action from the photo editor with PNG masking
 * Creates a pre-masked image that can be sent directly to the Node.js function
 * This eliminates the need for complex masking operations in the backend
 */
export interface SaveHandlerOptions {
  // Single zone mode
  imageUrl: string | null;
  selectedImage: File | null;
  rotation: number;
  scale: number;
  flipX: boolean;
  offsetX: number;
  offsetY: number;

  // Multi-zone mode
  zones?: ZoneConfig[];
  zoneImages?: Map<number, ZoneImageState>;
  isMultiZoneMode?: boolean;

  // Doodle mode
  doodle?: boolean;
  doodleCanvasRef?: RefObject<DoodleCanvasRef | null>;

  // Common options
  onFileSelect:
    | ((
        data: {
          userImage: { base64: string; file: File };
          editedImage: { base64: string; file: File };
          outlinedImage?: { base64: string; file: File };
        } | null
      ) => void)
    | undefined;
  stepImageDimensions: StepImageDimensions | null;
  uploadType: 'photo' | 'engraving';
  maskSvgBlob: Blob | null;
  maskImageConfig?: { url: string; width: number; height: number };
  setIsProcessing: (value: boolean) => void;
  setIsEditorOpen: (value: boolean) => void;
  setImageUrl: (value: string | null) => void;
  setError: (error: string) => void;
  positioning?: { x: number; y: number; scale: number; rotation: number };
  showStarMapSlot?: boolean;
}

export const createSaveHandler = (options: SaveHandlerOptions): (() => void) => {
  const {
    imageUrl,
    selectedImage,
    rotation,
    scale,
    flipX,
    offsetX,
    offsetY,
    zones = [],
    zoneImages = new Map(),
    isMultiZoneMode = false,
    doodle = false,
    doodleCanvasRef,
    onFileSelect,
    stepImageDimensions,
    uploadType,
    maskSvgBlob,
    maskImageConfig,
    setIsProcessing,
    setIsEditorOpen,
    setImageUrl,
    setError,
    positioning,
    showStarMapSlot = false
  } = options;

  const shouldDrawOutline = (hasMask: boolean) =>
    uploadType !== 'engraving' && !showStarMapSlot && hasMask;

  const canvasToBlob = (target: HTMLCanvasElement): Promise<Blob | null> =>
    new Promise((resolve) => target.toBlob(resolve, imageType, 0.7));

  const generateOutlinedBlob = async (
    canvas: HTMLCanvasElement,
    ctx: CanvasRenderingContext2D,
    width: number,
    height: number
  ): Promise<Blob | null> => {
    if (!maskSvgBlob) return null;
    const maskImage = await loadPNGMask({ maskSvgBlob });
    const maskPositioning = calculatePNGMaskPositioning({
      containerWidth: width,
      containerHeight: height,
      positioning
    });
    drawMaskOutlineOnCanvas({ ctx, maskImage, positioning: maskPositioning });
    return canvasToBlob(canvas);
  };

  return async () => {
    setIsProcessing(true);

    try {
      // Setup canvas with standardized dimensions
      const { canvas, ctx, actualWidth, actualHeight } = setupCanvas({ stepImageDimensions });

      // Doodle mode: export Konva stage and save
      if (doodle && doodleCanvasRef?.current) {
        const doodleDataUrl = doodleCanvasRef.current.exportToDataUrl();

        if (!doodleDataUrl) {
          setError('Failed to export doodle drawing. Please try again.');
          setIsProcessing(false);
          return;
        }

        const doodleImg = await loadImage({ src: doodleDataUrl });

        // Scale to cover while maintaining aspect ratio
        const doodleAspect = doodleImg.width / doodleImg.height;
        const canvasAspect = actualWidth / actualHeight;

        let drawWidth: number, drawHeight: number, drawX: number, drawY: number;

        if (doodleAspect > canvasAspect) {
          drawHeight = actualHeight;
          drawWidth = actualHeight * doodleAspect;
          drawX = (actualWidth - drawWidth) / 2;
          drawY = 0;
        } else {
          drawWidth = actualWidth;
          drawHeight = actualWidth / doodleAspect;
          drawX = 0;
          drawY = (actualHeight - drawHeight) / 2;
        }

        ctx.drawImage(doodleImg, drawX, drawY, drawWidth, drawHeight);

        if (maskSvgBlob) {
          try {
            const maskImage = await loadPNGMask({ maskSvgBlob });
            const maskPositioning = calculatePNGMaskPositioning({
              containerWidth: actualWidth,
              containerHeight: actualHeight,
              positioning
            });
            applyPNGMaskClipping({ ctx, maskImage, positioning: maskPositioning });

            ctx.save();
            ctx.globalCompositeOperation = 'destination-over';
            ctx.fillStyle = 'white';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            ctx.restore();
          } catch (maskError) {
            logError('Error applying mask to doodle', maskError, {});
          }
        }

        canvas.toBlob(
          async (blob) => {
            if (blob) {
              try {
                const editedFile = new File([blob], `doodle-drawing.${imageType.split('/').at(1)}`, {
                  type: imageType
                });
                const base64 = await fileToBase64(editedFile);
                const doodleFile = new File([blob], `doodle-original.${imageType.split('/').at(1)}`, {
                  type: imageType
                });

                if (onFileSelect) {
                  onFileSelect({
                    userImage: { base64, file: doodleFile },
                    editedImage: { base64, file: editedFile }
                  });
                }
                setIsEditorOpen(false);
              } catch (error) {
                logError('Error processing doodle save result', error, { hasBlob: !!blob });
                setError('Failed to process the doodle drawing. Please try again.');
              }
            } else {
              setError('Failed to create image from doodle drawing.');
            }
            setIsProcessing(false);
          },
          imageType,
          0.7
        );
        return;
      }

      if (isMultiZoneMode && zones.length > 0 && zoneImages.size > 0 && maskSvgBlob && maskImageConfig) {
        // Multi-zone mode: render all zones
        await renderAllZones(ctx, {
          zones,
          zoneImages,
          stepImageDimensions,
          uploadType,
          maskSvgBlob,
          maskImageConfig,
          positioning,
          showStarMapSlot,
          actualWidth,
          actualHeight
        });

        // Get the first zone's file for naming (or use a default name)
        const firstZoneImage = Array.from(zoneImages.values())[0];
        const outputFileName = firstZoneImage?.file.name || `composite-image.${imageType.split('/').at(1)}`;

        try {
          // Snapshot without outline
          const blob = await canvasToBlob(canvas);
          if (!blob) return;

          const editedFile = new File([blob], outputFileName, { type: imageType });
          const base64 = await fileToBase64(editedFile);

          let outlinedResult: { base64: string; file: File } | undefined;
          if (shouldDrawOutline(true)) {
            const outlinedBlob = await generateOutlinedBlob(canvas, ctx, actualWidth, actualHeight);
            if (outlinedBlob) {
              const outlinedFile = new File([outlinedBlob], outputFileName, { type: imageType });
              outlinedResult = { base64: await fileToBase64(outlinedFile), file: outlinedFile };
            }
          }

          if (onFileSelect) {
            const userImageFile = firstZoneImage?.file || new File([], `composite.${imageType}`);
            const userImageBase64 = firstZoneImage ? await fileToBase64(userImageFile) : base64;

            onFileSelect({
              userImage: { base64: userImageBase64, file: userImageFile },
              editedImage: { base64, file: editedFile },
              ...(outlinedResult && { outlinedImage: outlinedResult })
            });
          }
          setIsEditorOpen(false);
        } catch (error) {
          logError('Error processing multi-zone save result', error, {
            zoneCount: zones.length
          });
          setError('Failed to process the saved image. Please try again.');
        } finally {
          setIsProcessing(false);
        }
      } else if (!isMultiZoneMode && imageUrl && selectedImage) {
        // Single zone mode: existing behavior
        const isCurrentImageStarMap = selectedImage?.name === `star-map.${imageType}` || selectedImage?.name === `location-map.${imageType}` || showStarMapSlot || false;

        // Load the user's uploaded image
        const uploadedImg = await loadImage({ src: imageUrl });

        // Calculate image scaling
        const { scaledWidth, scaledHeight } = calculateImageScaling({
          image: uploadedImg,
          canvasWidth: actualWidth,
          canvasHeight: actualHeight
        });

        // Render with or without mask
        if (maskSvgBlob) {
          await renderUserImageWithMask(ctx, uploadedImg, scaledWidth, scaledHeight, {
            rotation,
            scale,
            flipX,
            offsetX,
            offsetY,
            uploadType,
            showStarMapSlot: isCurrentImageStarMap,
            maskSvgBlob,
            positioning,
            actualWidth,
            actualHeight
          });
        } else {
          renderUserImageWithoutMask(ctx, uploadedImg, scaledWidth, scaledHeight, {
            rotation,
            scale,
            flipX,
            offsetX,
            offsetY,
            uploadType,
            showStarMapSlot: isCurrentImageStarMap
          });
        }

        try {
          // Snapshot without outline
          const blob = await canvasToBlob(canvas);
          if (!blob) return;

          const filenameWithoutExtension = selectedImage.name.split('.').at(0);
          const fileName = `${filenameWithoutExtension}.${imageType.split('/').at(1)}`;
          const editedFile = new File([blob], fileName, { type: imageType });
          const base64 = await fileToBase64(editedFile);

          let outlinedResult: { base64: string; file: File } | undefined;
          if (shouldDrawOutline(!!maskSvgBlob) && !isCurrentImageStarMap) {
            const outlinedBlob = await generateOutlinedBlob(canvas, ctx, actualWidth, actualHeight);
            if (outlinedBlob) {
              const outlinedFile = new File([outlinedBlob], fileName, { type: imageType });
              outlinedResult = { base64: await fileToBase64(outlinedFile), file: outlinedFile };
            }
          }

          if (onFileSelect) {
            const originalBase64 = await fileToBase64(selectedImage);
            onFileSelect({
              userImage: { base64: originalBase64, file: selectedImage },
              editedImage: { base64, file: editedFile },
              ...(outlinedResult && { outlinedImage: outlinedResult })
            });
          }
          setIsEditorOpen(false);
          if (imageUrl) {
            URL.revokeObjectURL(imageUrl);
            setImageUrl(null);
          }
        } catch (error) {
          logError('Error processing save result', error, {
            fileName: selectedImage.name
          });
          setError('Failed to process the saved image. Please try again.');
        } finally {
          setIsProcessing(false);
        }
      } else {
        setError('No images to save');
        setIsProcessing(false);
      }
    } catch (error) {
      logError('Error in save handler', error, {
        isMultiZoneMode,
        imageUrl: !!imageUrl,
        hasMask: !!maskSvgBlob,
        uploadType
      });
      setError('Failed to save the image. Please try again.');
      setIsProcessing(false);
    }
  };
};
